/**
 * Schedule RTK Query — téléphonie/relance + rendez-vous (docs/06-TELEPHONIE-RDV.md).
 * Endpoints Worker : /telephony/config, /calls/:id/outbound, /outbound,
 * /appointments (CRUD + availability).
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { env } from '../../config/env';
import { loadStoredToken } from '../../features/auth/model/session.slice';

async function scheduleRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const token = loadStoredToken();
  const res = await fetch(`${env.apiUrl}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const body = (await res.json().catch(() => null)) as { data?: T; error?: { message?: string } } | null;
  if (!res.ok || body?.error) {
    throw new Error(body?.error?.message ?? `Erreur API (${res.status})`);
  }
  return body?.data as T;
}

export interface TelephonyConfig {
  tenantId: string;
  provider: 'twilio' | 'telnyx';
  phoneNumber: string;
  accountSid: string;
  outboundEnabled: boolean;
  outboundMaxPerDay: number;
}

export interface OutboundCall {
  id: string;
  callId?: string;
  leadId?: string;
  toPhone: string;
  purpose: 'confirmation_rdv' | 'retour_devis' | 'retour_information' | 'rappel_paiement';
  status: 'queued' | 'dialing' | 'in_progress' | 'completed' | 'failed' | 'no_answer' | 'canceled';
  summary?: string;
  scheduledAt?: string;
  createdAt: string;
}

export interface Appointment {
  id: string;
  leadId?: string;
  callId?: string;
  clientName: string;
  clientPhone?: string;
  clientEmail?: string;
  title: string;
  startsAt: string;
  endsAt: string;
  status: 'pending' | 'confirmed' | 'completed' | 'canceled' | 'no_show';
  createdBy: 'agent_vocal' | 'professionnel' | 'client';
  notes?: string;
  createdAt: string;
  timezone?: string;
  version?: number;
}

export interface AppointmentSlot {
  startsAt: string;
  endsAt: string;
  timezone?: string;
}

export interface CalendarAppointmentType {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  slotIntervalMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  minimumNoticeMinutes: number;
  bookingWindowDays: number;
  voiceBookingEnabled: boolean;
  active: boolean;
}

export interface CalendarRule {
  id?: string;
  weekday: number;
  startTime: string;
  endTime: string;
}

export interface CalendarException {
  id: string;
  localDate: string;
  kind: 'available' | 'unavailable';
  startTime: string | null;
  endTime: string | null;
  reason: string | null;
}

export interface CalendarSettings {
  schedule: { id: string; name: string; timezone: string; active: boolean };
  types: CalendarAppointmentType[];
  rules: CalendarRule[];
  exceptions: CalendarException[];
}

export const scheduleApi = createApi({
  reducerPath: 'scheduleApi',
  baseQuery: async () => ({ data: null as unknown }),
  tagTypes: ['Appointments', 'Outbound', 'TelephonyConfig', 'CalendarSettings'],
  endpoints: (builder) => ({
    getTelephonyConfig: builder.query<TelephonyConfig | null, void>({
      queryFn: async () => {
        try {
          return { data: await scheduleRequest<TelephonyConfig | null>('/telephony/config') };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['TelephonyConfig'],
    }),
    updateTelephonyConfig: builder.mutation<TelephonyConfig, Partial<TelephonyConfig>>({
      queryFn: async (config) => {
        try {
          return { data: await scheduleRequest<TelephonyConfig>('/telephony/config', {
            method: 'PUT',
            body: JSON.stringify(config),
          }) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['TelephonyConfig'],
    }),

    listOutbound: builder.query<OutboundCall[], { status?: string } | void>({
      queryFn: async (args) => {
        try {
          const params = new URLSearchParams();
          if (args && 'status' in args && args.status) params.set('status', args.status);
          return { data: await scheduleRequest<OutboundCall[]>(`/outbound?${params}`) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['Outbound'],
    }),
    /** Relance l'agent vocal vers le client (immédiate, ou programmée via scheduledAt). */
    requestOutbound: builder.mutation<OutboundCall, {
      /** Identifiant du lead (pipeline) — résolu en appel d'origine côté Worker. */
      leadId: string;
      purpose: OutboundCall['purpose'];
      toPhone?: string;
      scheduledAt?: string;
      idempotencyKey?: string;
    }>({
      queryFn: async ({ leadId, ...body }) => {
        try {
          return { data: await scheduleRequest<OutboundCall>(`/leads/${leadId}/outbound`, {
            method: 'POST',
            body: JSON.stringify(body),
          }) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['Outbound'],
    }),
    cancelOutbound: builder.mutation<OutboundCall, string>({
      queryFn: async (outboundId) => {
        try {
          return { data: await scheduleRequest<OutboundCall>(`/outbound/${outboundId}/cancel`, { method: 'POST' }) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['Outbound'],
    }),

    listAppointments: builder.query<Appointment[], { from?: string; to?: string } | void>({
      queryFn: async (args) => {
        try {
          const params = new URLSearchParams();
          if (args && 'from' in args && args.from) params.set('from', args.from);
          if (args && 'to' in args && args.to) params.set('to', args.to);
          return { data: await scheduleRequest<Appointment[]>(`/appointments?${params}`) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      providesTags: ['Appointments'],
    }),
    getAppointmentAvailability: builder.query<AppointmentSlot[], { date: string; durationMinutes?: number }>({
      queryFn: async ({ date, durationMinutes = 30 }) => {
        try {
          const params = new URLSearchParams({ date, durationMinutes: String(durationMinutes) });
          return { data: await scheduleRequest<AppointmentSlot[]>(`/appointments/availability?${params}`) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
    }),
    getCalendarSettings: builder.query<CalendarSettings, void>({
      queryFn: async () => {
        try { return { data: await scheduleRequest<CalendarSettings>('/calendar/settings') }; }
        catch (e) { return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } }; }
      },
      providesTags: ['CalendarSettings'],
    }),
    updateCalendarSettings: builder.mutation<CalendarSettings, Pick<CalendarSettings['schedule'], 'name' | 'timezone' | 'active'> & { rules: CalendarRule[] }>({
      queryFn: async (body) => {
        try { return { data: await scheduleRequest<CalendarSettings>('/calendar/settings', { method: 'PUT', body: JSON.stringify(body) }) }; }
        catch (e) { return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } }; }
      },
      invalidatesTags: ['CalendarSettings', 'Appointments'],
    }),
    createCalendarAppointmentType: builder.mutation<CalendarAppointmentType, Omit<CalendarAppointmentType, 'id'>>({
      queryFn: async (body) => {
        try { return { data: await scheduleRequest<CalendarAppointmentType>('/calendar/settings/types', { method: 'POST', body: JSON.stringify(body) }) }; }
        catch (e) { return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } }; }
      },
      invalidatesTags: ['CalendarSettings'],
    }),
    updateCalendarAppointmentType: builder.mutation<CalendarAppointmentType, CalendarAppointmentType>({
      queryFn: async ({ id, ...body }) => {
        try { return { data: await scheduleRequest<CalendarAppointmentType>(`/calendar/settings/types/${id}`, { method: 'PATCH', body: JSON.stringify(body) }) }; }
        catch (e) { return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } }; }
      },
      invalidatesTags: ['CalendarSettings'],
    }),
    archiveCalendarAppointmentType: builder.mutation<{ archived: boolean; id: string }, string>({
      queryFn: async (id) => {
        try { return { data: await scheduleRequest(`/calendar/settings/types/${id}`, { method: 'DELETE' }) }; }
        catch (e) { return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } }; }
      },
      invalidatesTags: ['CalendarSettings'],
    }),
    createCalendarException: builder.mutation<CalendarException, Omit<CalendarException, 'id'>>({
      queryFn: async (body) => {
        try { return { data: await scheduleRequest('/calendar/settings/exceptions', { method: 'POST', body: JSON.stringify(body) }) }; }
        catch (e) { return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } }; }
      },
      invalidatesTags: ['CalendarSettings', 'Appointments'],
    }),
    updateCalendarException: builder.mutation<CalendarException, CalendarException>({
      queryFn: async ({ id, ...body }) => {
        try { return { data: await scheduleRequest(`/calendar/settings/exceptions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }) }; }
        catch (e) { return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } }; }
      },
      invalidatesTags: ['CalendarSettings', 'Appointments'],
    }),
    deleteCalendarException: builder.mutation<{ deleted: boolean; id: string }, string>({
      queryFn: async (id) => {
        try { return { data: await scheduleRequest(`/calendar/settings/exceptions/${id}`, { method: 'DELETE' }) }; }
        catch (e) { return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } }; }
      },
      invalidatesTags: ['CalendarSettings', 'Appointments'],
    }),
    createAppointment: builder.mutation<Appointment, {
      clientName: string;
      clientPhone?: string;
      title?: string;
      startsAt: string;
      endsAt: string;
      notes?: string;
      idempotencyKey?: string;
    }>({
      queryFn: async ({ idempotencyKey, ...body }) => {
        try {
          return { data: await scheduleRequest<Appointment>('/appointments', {
            method: 'POST',
            body: JSON.stringify(body),
            headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
          }) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['Appointments'],
    }),
    updateAppointment: builder.mutation<Appointment, {
      appointmentId: string;
      startsAt?: string;
      endsAt?: string;
      status?: Appointment['status'];
      notes?: string;
      idempotencyKey?: string;
    }>({
      queryFn: async ({ appointmentId, idempotencyKey, ...body }) => {
        try {
          return { data: await scheduleRequest<Appointment>(`/appointments/${appointmentId}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
            headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
          }) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['Appointments'],
    }),
    cancelAppointment: builder.mutation<Appointment, string>({
      queryFn: async (appointmentId) => {
        try {
          return { data: await scheduleRequest<Appointment>(`/appointments/${appointmentId}`, {
            method: 'DELETE',
          }) };
        } catch (e) {
          return { error: { status: 'CUSTOM_ERROR', error: (e as Error).message } };
        }
      },
      invalidatesTags: ['Appointments'],
    }),
  }),
});

export const {
  useGetTelephonyConfigQuery,
  useUpdateTelephonyConfigMutation,
  useListOutboundQuery,
  useRequestOutboundMutation,
  useCancelOutboundMutation,
  useListAppointmentsQuery,
  useLazyGetAppointmentAvailabilityQuery,
  useGetCalendarSettingsQuery,
  useUpdateCalendarSettingsMutation,
  useCreateCalendarAppointmentTypeMutation,
  useUpdateCalendarAppointmentTypeMutation,
  useArchiveCalendarAppointmentTypeMutation,
  useCreateCalendarExceptionMutation,
  useUpdateCalendarExceptionMutation,
  useDeleteCalendarExceptionMutation,
  useCreateAppointmentMutation,
  useUpdateAppointmentMutation,
  useCancelAppointmentMutation,
} = scheduleApi;
